/**
 * Site create/edit DrawerPanel form.
 *
 * Slide-out form for creating/editing Sites (tenant/mine). Mirrors MachineDrawerForm.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { TextField, Box } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DrawerPanel } from '../../components/shared';
import { useNotification } from '../../providers/NotificationProvider';
import type { DrawerAction } from '../../components/shared';
import { SiteControllerApi } from '../../api-generated/apis';
import type { Site } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

const siteApi = new SiteControllerApi(apiConfig);

export interface SiteDrawerFormProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  siteId?: string;
}

interface SiteFormData {
  name: string;
  customerName: string;
  location: string;
  code: string;
}

interface FormErrors {
  name?: string;
}

const INITIAL: SiteFormData = { name: '', customerName: '', location: '', code: '' };

function validate(data: SiteFormData): FormErrors {
  const errors: FormErrors = {};
  if (!data.name.trim()) {
    errors.name = 'Name is required';
  } else if (data.name.length > 100) {
    errors.name = 'Name must not exceed 100 characters';
  }
  return errors;
}

export const SiteDrawerForm: React.FC<SiteDrawerFormProps> = ({ open, onClose, mode, siteId }) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  const [formData, setFormData] = useState<SiteFormData>(INITIAL);
  const [errors, setErrors] = useState<FormErrors>({});

  const siteQuery = useQuery<Site>({
    queryKey: ['site', siteId],
    queryFn: () => siteApi.getSite({ id: siteId! }),
    enabled: open && mode === 'edit' && !!siteId,
  });

  useEffect(() => {
    if (mode === 'edit' && siteQuery.data) {
      setFormData({
        name: siteQuery.data.name ?? '',
        customerName: siteQuery.data.customerName ?? '',
        location: siteQuery.data.location ?? '',
        code: siteQuery.data.code ?? '',
      });
      setErrors({});
    }
  }, [mode, siteQuery.data]);

  useEffect(() => {
    if (open && mode === 'create') {
      setFormData(INITIAL);
      setErrors({});
    }
  }, [open, mode]);

  const createMutation = useMutation({
    mutationFn: (data: SiteFormData) =>
      siteApi.createSite({
        site: {
          name: data.name,
          customerName: data.customerName || undefined,
          location: data.location || undefined,
          code: data.code || undefined,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      onClose();
      notification.showSuccess('Site created successfully');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: SiteFormData) =>
      siteApi.updateSite({
        id: siteId!,
        site: {
          name: data.name,
          customerName: data.customerName || undefined,
          location: data.location || undefined,
          code: data.code || undefined,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      onClose();
      notification.showSuccess('Site updated successfully');
    },
  });

  const handleFieldChange = useCallback(
    (field: keyof SiteFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    const validationErrors = validate(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    if (mode === 'create') createMutation.mutate(formData);
    else updateMutation.mutate(formData);
  }, [formData, mode, createMutation, updateMutation]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const actions: DrawerAction[] = [
    { label: 'Cancel', variant: 'outlined', onClick: onClose, disabled: isSaving, testId: 'site-form-cancel' },
    { label: 'Save', variant: 'contained', onClick: handleSubmit, disabled: isSaving, color: 'primary', testId: 'site-form-save' },
  ];

  const isLoading = mode === 'edit' && siteQuery.isLoading;

  return (
    <DrawerPanel
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add Site' : 'Edit Site'}
      width="narrow"
      actions={actions}
      loading={isLoading}
      testId="site-form"
    >
      <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          label="Name"
          value={formData.name}
          onChange={handleFieldChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          required
          fullWidth
          inputProps={{ maxLength: 100, 'data-testid': 'site-form-name' }}
        />
        <TextField label="Customer Name" value={formData.customerName} onChange={handleFieldChange('customerName')} fullWidth inputProps={{ 'data-testid': 'site-form-customerName' }} />
        <TextField label="Location" value={formData.location} onChange={handleFieldChange('location')} fullWidth inputProps={{ 'data-testid': 'site-form-location' }} />
        <TextField label="Code" value={formData.code} onChange={handleFieldChange('code')} fullWidth inputProps={{ 'data-testid': 'site-form-code' }} />
      </Box>
    </DrawerPanel>
  );
};

export default SiteDrawerForm;
